import { CLIENT_ERROR_PAYLOAD_PATTERN } from "../sentry-pii-scrub";

export type ErrorWithStatusCode = Error & { statusCode?: number };

/**
 * Default policy: report mutation errors to Sentry when they match:
 * - 5xx (server errors)
 * - Network failures (no statusCode, e.g. fetch rejected)
 * - 4xx 400/422 from controlled flows (we own/validate the request)
 * Do not report 401/404 (noisy or expected).
 */
export function defaultShouldReportMutationError(error: unknown): boolean {
  const err = error as ErrorWithStatusCode | undefined;
  if (!err) return false;
  const status = err.statusCode;
  if (status == null) return true;
  if (status >= 500) return true;
  if (status === 400 || status === 422) return true;
  return false;
}

/** True when the query error looks like a REST client 400 (e.g. axios/fetch error shape). */
export function shouldCaptureRest400QueryError(error: unknown): boolean {
  if (error == null) return false;
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return status != null && String(status) === "400";
}

export type GraphQlFormattedError = {
  message?: string;
  path?: string[];
  extensions?: Record<string, unknown>;
};

export type SentryCaptureException = (
  exception: unknown,
  captureContext?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  },
) => string;

/** GraphQL errors embedded in a client error (`error.response.errors`). */
export function getGraphQlErrorFromUnknown(
  error: unknown,
): GraphQlFormattedError | null {
  const first = (error as { response?: { errors?: GraphQlFormattedError[] } })
    ?.response?.errors?.[0];
  return first ?? null;
}

/**
 * Name + message safe to put in a Sentry event for an arbitrary thrown
 * value. Never returns a graphql-request ClientError's raw `.message`, which
 * embeds the full request including variables (CRM-NEXTJS-B9): prefers the
 * first GraphQL error's message, else strips the serialized payload.
 */
export function toSafeReportedError(error: unknown): {
  name: string;
  message: string;
} {
  const name = error instanceof Error ? error.name : "Error";
  const gqlMessage = getGraphQlErrorFromUnknown(error)?.message;
  if (gqlMessage) {
    return { name, message: gqlMessage };
  }
  const rawMessage = error instanceof Error ? error.message : String(error);
  const payloadStart = CLIENT_ERROR_PAYLOAD_PATTERN.exec(rawMessage)?.index;
  return {
    name,
    message:
      payloadStart === undefined
        ? rawMessage
        : rawMessage.slice(0, payloadStart),
  };
}

/**
 * The thrown error's stack with its raw `.message` swapped for
 * `safeMessage`. V8 stacks start with `${name}: ${message}`, so copying a
 * ClientError's stack as-is would carry the serialized request along with it.
 */
export function toSafeStack(
  error: unknown,
  safeMessage: string,
): string | undefined {
  if (!(error instanceof Error) || !error.stack) return undefined;
  if (!error.message) return error.stack;
  // Function replacer: a string replacement would expand `$&`-style patterns.
  return error.stack.replace(error.message, () => safeMessage);
}

/**
 * Operation name from a graphql-request error's `request.query` (e.g.
 * "updateContactInfo"). Reads only the query text, never `variables`. An
 * HTTP-level failure (e.g. a bare 400) carries no GraphQL `errors[].path`,
 * so this is the only safe signal left for which operation failed once the
 * serialized request is stripped from the message.
 */
export function graphQlOperationName(error: unknown): string | null {
  const query = (error as { request?: { query?: unknown } } | null)?.request
    ?.query;
  if (typeof query !== "string") return null;
  const match =
    /\b(?:query|mutation|subscription)\s+([_A-Za-z][_0-9A-Za-z]*)/.exec(query);
  return match?.[1] ?? null;
}

/** GraphQL errors embedded in a 200 response (TanStack / Apollo-style `error.response.errors`). */
export function getFirstGraphQlErrorFromAction(
  action:
    | { error?: { response?: { errors?: GraphQlFormattedError[] } } }
    | undefined,
): GraphQlFormattedError | null {
  return getGraphQlErrorFromUnknown(action?.error);
}

export function graphQlSentryStatusCode(error: GraphQlFormattedError): string {
  return String(
    error.extensions?.status ??
      (error.extensions?.originalError as { statusCode?: number } | undefined)
        ?.statusCode ??
      "",
  );
}

export function graphQlSentryErrorCode(error: GraphQlFormattedError): string {
  return String((error.extensions?.code as string) ?? "");
}

export function graphQlPathTag(path: string[] | undefined): string {
  return Array.isArray(path) ? path.join(".") : "";
}

/**
 * Prefer the thrown error's stack; otherwise join GraphQL `extensions.stacktrace` if present.
 */
export function resolveGraphQlSentryStack(
  actionErrorStack: string | undefined,
  graphqlError: GraphQlFormattedError,
): string | undefined {
  if (actionErrorStack) return actionErrorStack;
  const st = graphqlError.extensions?.stacktrace;
  if (Array.isArray(st)) return (st as string[]).join("\n");
  return undefined;
}

/** True when trimmed GraphQL `message` matches an entry in ignoredSet (exact match). */
export function shouldIgnoreGraphQlQueryMessage(
  message: string | undefined,
  ignoredSet: ReadonlySet<string>,
): boolean {
  if (!ignoredSet.size) return false;
  const key = (message ?? "").trim();
  return key !== "" && ignoredSet.has(key);
}

export type ReportGraphQlQueryErrorOptions = {
  error: unknown;
  queryKey: readonly unknown[];
  queryHash?: string;
  captureException: SentryCaptureException;
  ignoredGraphQlQueryMessages?: ReadonlySet<string>;
  extraTags?: Record<string, string>;
};

/**
 * Reports a GraphQL query failure to Sentry.
 * Use when a queryFn handles an error (e.g. returns a fallback) but the failure should still be visible.
 */
export function reportGraphQlQueryErrorToSentry(
  options: ReportGraphQlQueryErrorOptions,
): boolean {
  const {
    error,
    queryKey,
    queryHash,
    captureException,
    ignoredGraphQlQueryMessages,
    extraTags,
  } = options;

  const gqlError =
    getGraphQlErrorFromUnknown(error) ??
    (error instanceof Error
      ? { message: toSafeReportedError(error).message }
      : null);

  if (!gqlError?.message) {
    return false;
  }

  if (
    shouldIgnoreGraphQlQueryMessage(
      gqlError.message,
      ignoredGraphQlQueryMessages ?? new Set(),
    )
  ) {
    return false;
  }

  const actionErrorStack = error instanceof Error ? error.stack : undefined;
  const sentryError = new Error(gqlError.message);
  const stack = resolveGraphQlSentryStack(actionErrorStack, gqlError);
  if (stack) {
    sentryError.stack = stack;
  }

  const statusCode = graphQlSentryStatusCode(gqlError);
  captureException(sentryError, {
    tags: {
      errorSource: "tanstack-query",
      graphqlPath: graphQlPathTag(gqlError.path),
      statusCode,
      errorCode: graphQlSentryErrorCode(gqlError),
      ...extraTags,
    },
    extra: {
      timestamp: new Date().toISOString(),
      queryInfo: {
        queryKey,
        queryHash,
      },
      error: gqlError,
    },
  });

  return true;
}
